import {Component, EventEmitter, forwardRef, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Observable} from 'rxjs';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {AbstractValueAccessorComponent} from '../../component/value-accessor.component';
import {lodash} from '../../utils';
import {debounce} from "lodash";

/**
 * Component used to filter (search in) a list of results
 */
@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FilterComponent), multi: true }
    ],
    host: { 'class': 'input-group' },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FilterComponent extends AbstractValueAccessorComponent<string> implements OnInit {

  filterTerm: string;
  resetInProgress: boolean;

  @Input() searchFunction: (arg: string) => Observable<any[]> | any[];
  @Input() getAllFunction: (arg: void) => Observable<any[]> | any[];

  @Input() placeholder = '';
  @Input() disabled: boolean;
  @Input() minCharacters: number = 1;
  @Input() showTooltip = false;
  @Input() id;
  @Input() debounceTime: number = 0;

  @Output() resultsFound = new EventEmitter<any[]>();
  searching: boolean = false;

  searchDebounced: (oldValue: string) => any;

  ngOnInit() {
    this.searchDebounced = this.debounceTime ? debounce(this.search, this.debounceTime) : this.search;
    if (!this.searchFunction) {
      throw new Error('Attribute searchFunction is required for app-filter component');
    }
    if (!this.getAllFunction) {
      this.getAllFunction = () => this.searchFunction('');
    }
  }

  reset(): void {
    this.resetInProgress = true;
    this.onInput('');
  }

  get value(): string {
    return this.filterTerm;
  }

  writeValue(value: string): void {
    if (this.resetInProgress === false) {
      this.filterTerm = '';
    } else {
      this.filterTerm = value;
    }
  }

  onInput(value: string) {
    const oldValue = this.filterTerm;
    this.filterTerm = value || '';
    this.onModelChange();
    this.searchDebounced(oldValue);
  }

  search = (oldValue: string) => {
    if (this.searching) {
      return;
    }

    this.searching = true;
    this.resetInProgress = false;
    const lastTerm = this.filterTerm;

    const termMeetsMinimum = lastTerm && lastTerm.length >= this.minCharacters;
    const emtpyTermIsSearchable = lastTerm?.length === 0 && this.minCharacters == 0;
    const termJustCleared = lastTerm?.length === 0 && oldValue

    let pendingResults: Observable<any[]> | any[];
    if (termMeetsMinimum || emtpyTermIsSearchable) {
      pendingResults = this.searchFunction(lastTerm);
    } else if (termJustCleared) {
      pendingResults = this.getAllFunction();
    } else {
      // No need to search
      this.searching = false;
      return;
    }

    // If results are already present and directly given in list, no new search required
    if (lodash.isArray(pendingResults)) {
      this.onResults(<any[]> pendingResults);
    } else {
      pendingResults.subscribe((results : any[]) => {
        this.onResults(results);
        if (lastTerm !== this.filterTerm) {
          this.searchDebounced(lastTerm);
        }
      })
    }
  };

  private onResults(results : any[]) {
    this.resultsFound.emit(results);
    this.searching = false;
  }

  protected onFocus() {
    if (this.minCharacters === 0) {
      this.filterTerm = '';
      this.searchDebounced('');
    }
  }
}
